<script setup>
import { ref, watch } from 'vue';
import { fetchData } from '../../fetchData';
import ColumnBanner from './ColumnBanner.vue';
import NewsListItem from './NewsListItem.vue';
import NewsListItemSkeleton from './NewsListItemSkeleton.vue';

const props = defineProps({
    postCategoryIds: {
        type: Array,
        default: () => []
    },
    searchPhrase: {
        type: String,
        default: ''
    },
    sortBy: {
        type: String,
        default: 'published_at:desc'
    },
    authorIds: {
        type: Array,
        default: () => []
    },
    dateRange: {
        type: Array,
        default: () => []
    }
});
const emit = defineEmits(['request-done']);
const SCROLL_OFFSET = 80;
const posts = ref([]);
const initialized = ref(true);
const loading = ref(false);
const loadingMore = ref(false);
const error = ref(null);
const currentPage = ref(1);
const hasNextPage = ref(false);
const isAnyFilterActive = ref(false)

const getFormattedDateRange = (dateRange) => {
    const startDate = new Date(dateRange[0]);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(dateRange[1]);
    endDate.setHours(23, 59, 59, 999);

    return [startDate.toISOString(), endDate.toISOString()];
};

const fetchNews = async (isLoadMore = false) => {
    if (isLoadMore) {
        loadingMore.value = true;
    } else {
        loading.value = true;
        currentPage.value = 1;
    }

    error.value = null;

    const params = new URLSearchParams();

    isAnyFilterActive.value = false;

    if (props.postCategoryIds.length) {
        props.postCategoryIds.forEach((id) => params.append('post_categories_id[]', id));
        isAnyFilterActive.value = true;
    }

    if (props.searchPhrase) {
        params.append('search_phrase', props.searchPhrase);
        isAnyFilterActive.value = true;
    }

    if (props.sortBy) {
        params.append('sort[by]', props.sortBy.split(':')[0]);
        params.append('sort[order]', props.sortBy.split(':')[1]);
    }

    if (props.authorIds.length) {
        props.authorIds.forEach((id) => params.append('user_id[]', id));
        isAnyFilterActive.value = true;
    }

    if (props.dateRange.length) {
        params.append('published_at[]', getFormattedDateRange(props.dateRange)[0]);
        params.append('published_at[]', getFormattedDateRange(props.dateRange)[1]);
        isAnyFilterActive.value = true;
    }

    params.append('page', currentPage.value);
    params.append('limit', 100);

    if (isAnyFilterActive.value === false) {
        params.append('highlighted', false);
    }

    try {
        const { result = {} } = await fetchData(`/api/v1/posts?${params.toString()}`);

        if (isLoadMore) {
            posts.value = [...posts.value, ...result.data];
        } else {
            posts.value = result.data;
        }

        hasNextPage.value = result.meta.current_page < result.meta.last_page;
    } catch (e) {
        error.value = e.message;
    } finally {
        loading.value = false;
        loadingMore.value = false;

        if (initialized.value) {
            emit('request-done', { isLoadMore });
        }

        initialized.value = true;
    }
};

const loadMore = () => {
    if (hasNextPage.value) {
        currentPage.value++;
        fetchNews(true);
    }
};

let debounceTimeout = null;
let firstRun = true;

const debouncedFetchNews = () => {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
        const target = document.getElementById('news-wrapper');

        if (!firstRun) {
            if (target) {
                const y = target.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET;
                window.scrollTo({ top: y < 0 ? 0 : y, behavior: 'smooth' });
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        }

        fetchNews();

        if (firstRun) firstRun = false;
    }, 900);
};

watch(
    () => [props.postCategoryIds, props.searchPhrase, props.sortBy, props.authorIds, props.dateRange],
    () => {
        debouncedFetchNews();
    },
    { deep: true, immediate: true }
);
</script>

<template>
    <div class="space-y-4">
        <div v-if="loading && !posts.length" class="space-y-4">
            <NewsListItemSkeleton v-for="n in 20" :key="n" />
        </div>

        <div v-else-if="error" class="text-center text-red-500 py-10">
            <p>{{ $t('error_no_items_found') }}</p>
        </div>

        <div v-else-if="posts.length === 0" class="text-center text-gray-500 py-10">
            <p>{{ $t('no_articles_founds') }}</p>
        </div>

        <div v-else class="space-y-4">
            <div v-for="(post, index) in posts" :key="post.id">
                <NewsListItem :key="post.id" :post="post"
                    class="shadow-md hover:shadow-xl rounded-xl transition-all ease-in-out" />
                <ColumnBanner v-if="isAnyFilterActive === false && (index + 1) % 5 === 0" :index="index"
                    :iteration="Math.floor((index + 1) / 5)" />
            </div>
        </div>

        <div v-if="hasNextPage" class="text-center">
            <button :disabled="loadingMore"
                class="bg-blue text-white px-6 py-2 rounded-full hover:bg-blue-dark transition disabled:bg-gray-300"
                @click="loadMore">
                {{ loadingMore ? $t('plain_loading') : $t('load_more') }}
            </button>
        </div>
    </div>
</template>
